import {
  FREQUENCY_MONTHS,
  scheduledValueAt,
  type Item,
  type Project,
  type ScenarioId,
  type TaxBracket,
} from '@finsim/model';

/** How the variants tree groups items: by the user's mental model, not by engine primitive. */
export const ITEM_GROUPS = [
  'income',
  'expense',
  'event',
  'transfer',
  'asset',
  'liability',
  'rule',
] as const;
export type ItemGroup = (typeof ITEM_GROUPS)[number];

export function itemGroup(item: Item): ItemGroup {
  switch (item.type) {
    case 'asset':
      return 'asset';
    case 'loan':
      return 'liability';
    case 'formula':
      return 'rule';
    case 'flow':
      if (item.account !== undefined) return 'transfer';
      if (item.frequency === 'once') return 'event';
      return item.direction === 'in' ? 'income' : 'expense';
  }
}

export type Cadence = 'month' | 'quarter' | 'halfYear' | 'year' | 'once';

const CADENCE: Record<number, Cadence> = {
  0: 'once',
  1: 'month',
  3: 'quarter',
  6: 'halfYear',
  12: 'year',
};

/** Highest marginal rate of a bracket set (the flat rate when there is one bracket). */
export function topRate(brackets: readonly TaxBracket[]): number {
  return brackets.at(-1)?.rate ?? 0;
}

export type ItemSummary =
  | { kind: 'money'; amount: number; sign: 1 | -1; cadence: Cadence | null }
  | { kind: 'percent'; percent: number; sources: number }
  | { kind: 'none' };

/** Headline figure for a tree row: the amount in force at month 0 (or at the item start). */
export function itemSummary(item: Item): ItemSummary {
  switch (item.type) {
    case 'flow': {
      const amount = scheduledValueAt(item.amount, item.start) ?? 0;
      return {
        kind: 'money',
        amount,
        sign: item.direction === 'in' ? 1 : -1,
        cadence: CADENCE[FREQUENCY_MONTHS[item.frequency]] ?? null,
      };
    }
    case 'formula':
      return {
        kind: 'percent',
        percent:
          item.spec.kind === 'tax'
            ? topRate(scheduledValueAt(item.spec.brackets, item.start) ?? [])
            : (scheduledValueAt(item.spec.percent, item.start) ?? 0),
        sources: item.spec.sources.length,
      };
    case 'asset':
      return { kind: 'money', amount: item.openingBalance, sign: 1, cadence: null };
    case 'loan':
      return { kind: 'money', amount: item.principal, sign: -1, cadence: null };
  }
}

/** Items of a variant, grouped in display order (empty groups omitted). */
export function groupedItems(
  project: Project,
  scenarioId: ScenarioId,
): { group: ItemGroup; items: Item[] }[] {
  const scenario = project.scenarios.find((s) => s.id === scenarioId);
  if (!scenario) return [];
  const byGroup = new Map<ItemGroup, Item[]>();
  for (const id of scenario.itemIds) {
    const item = project.items[id];
    if (!item) continue;
    const group = itemGroup(item);
    const list = byGroup.get(group) ?? [];
    list.push(item);
    byGroup.set(group, list);
  }
  return ITEM_GROUPS.flatMap((group) => {
    const items = byGroup.get(group);
    return items ? [{ group, items }] : [];
  });
}

/** Tree order of a variant's items (as displayed), for keyboard navigation. */
export function displayOrder(project: Project, scenarioId: ScenarioId): Item[] {
  return groupedItems(project, scenarioId).flatMap((g) => g.items);
}
